/**
 * HFDS — Headzflex Design System
 * Component: HF-010 Size Guide Modal
 * Version: 1.0.0
 *
 * Responsibilities:
 * - Open and close the global size guide.
 * - Trap and restore keyboard focus.
 * - Convert inches to centimeters.
 * - Support multiple size-guide triggers.
 * - Support Shopify Theme Editor reloads.
 */

(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-hf-size-guide]";
  const DIALOG_SELECTOR = "[data-hf-size-guide-dialog]";
  const OPEN_SELECTOR =
    '[data-hf-action="open-size-guide"]';
  const CLOSE_SELECTOR = "[data-hf-size-guide-close]";
  const UNIT_SELECTOR = "[data-hf-size-unit]";
  const VALUE_SELECTOR = "[data-hf-size-value]";

  class HFSizeGuide {
    /**
     * @param {HTMLElement} root
     */
    constructor(root) {
      this.root = root;
      this.dialog = root.querySelector(DIALOG_SELECTOR);
      this.closeButtons = Array.from(
        root.querySelectorAll(CLOSE_SELECTOR)
      );
      this.unitButtons = Array.from(
        root.querySelectorAll(UNIT_SELECTOR)
      );
      this.valueElements = Array.from(
        root.querySelectorAll(VALUE_SELECTOR)
      );

      this.currentUnit =
        root.dataset.defaultUnit === "cm"
          ? "cm"
          : "in";

      this.focusCleanup = null;
      this.lastTrigger = null;

      this.handleDocumentClick =
        this.handleDocumentClick.bind(this);

      this.handleCloseClick =
        this.handleCloseClick.bind(this);

      this.handleDialogClick =
        this.handleDialogClick.bind(this);

      this.handleDialogClose =
        this.handleDialogClose.bind(this);

      this.handleUnitClick =
        this.handleUnitClick.bind(this);

      this.initialize();
    }

    initialize() {
      if (!window.HFDS) {
        console.error(
          "[HFDS Size Guide] HFDS core is unavailable."
        );

        return;
      }

      if (!(this.dialog instanceof HTMLDialogElement)) {
        console.error(
          "[HFDS Size Guide] Dialog element was not found."
        );

        return;
      }

      document.addEventListener(
        "click",
        this.handleDocumentClick
      );

      this.closeButtons.forEach((button) => {
        button.addEventListener(
          "click",
          this.handleCloseClick
        );
      });

      this.dialog.addEventListener(
        "click",
        this.handleDialogClick
      );

      this.dialog.addEventListener(
        "close",
        this.handleDialogClose
      );

      this.unitButtons.forEach((button) => {
        button.addEventListener(
          "click",
          this.handleUnitClick
        );
      });

      this.updateUnits(this.currentUnit, {
        announce: false
      });

      this.root.dataset.hfInitialized = "true";
    }

    /**
     * Uses event delegation so buttons rendered by other
     * Shopify sections continue to work after section reloads.
     *
     * @param {MouseEvent} event
     */
    handleDocumentClick(event) {
      const trigger = event.target.closest(OPEN_SELECTOR);

      if (!(trigger instanceof HTMLElement)) {
        return;
      }

      event.preventDefault();

      this.lastTrigger = trigger;
      this.open();
    }

    open() {
      if (this.dialog.open) {
        return;
      }

      this.dialog.showModal();
      window.HFDS.lockScroll();

      this.focusCleanup = window.HFDS.trapFocus(
        this.dialog
      );

      window.HFDS.emit(
        "size-guide:open",
        {
          unit: this.currentUnit,
          productId:
            this.root.dataset.productId || ""
        },
        this.root
      );

      window.HFDS.announce(
        "Size guide opened.",
        "polite"
      );
    }

    handleCloseClick() {
      this.close();
    }

    close() {
      if (this.dialog.open) {
        this.dialog.close();
      }
    }

    /**
     * Clicking the dialog backdrop closes the modal.
     *
     * @param {MouseEvent} event
     */
    handleDialogClick(event) {
      if (event.target === this.dialog) {
        this.close();
      }
    }

    handleDialogClose() {
      window.HFDS.unlockScroll();

      if (typeof this.focusCleanup === "function") {
        this.focusCleanup();
      }

      this.focusCleanup = null;

      if (
        this.lastTrigger instanceof HTMLElement &&
        document.contains(this.lastTrigger)
      ) {
        this.lastTrigger.focus();
      }

      window.HFDS.emit(
        "size-guide:close",
        {
          unit: this.currentUnit,
          productId:
            this.root.dataset.productId || ""
        },
        this.root
      );
    }

    /**
     * @param {MouseEvent} event
     */
    handleUnitClick(event) {
      const button = event.currentTarget;
      const unit = button.dataset.hfSizeUnit;

      if (unit !== "in" && unit !== "cm") {
        return;
      }

      this.updateUnits(unit);
    }

    /**
     * @param {"in"|"cm"} unit
     * @param {Object} options
     */
    updateUnits(
      unit,
      {
        announce = true
      } = {}
    ) {
      this.currentUnit = unit;

      this.unitButtons.forEach((button) => {
        const active =
          button.dataset.hfSizeUnit === unit;

        button.classList.toggle(
          "is-active",
          active
        );

        button.setAttribute(
          "aria-pressed",
          String(active)
        );
      });

      this.valueElements.forEach((element) => {
        const inches = Number(
          element.dataset.inches
        );

        if (!Number.isFinite(inches)) {
          return;
        }

        element.textContent =
          unit === "cm"
            ? this.formatCentimeters(inches)
            : this.formatInches(inches);
      });

      this.root
        .querySelectorAll("[data-hf-unit-label]")
        .forEach((element) => {
          element.textContent =
            unit === "cm" ? "cm" : "in";
        });

      if (announce) {
        window.HFDS.announce(
          unit === "cm"
            ? "Measurements changed to centimeters."
            : "Measurements changed to inches.",
          "polite"
        );
      }

      window.HFDS.emit(
        "size-guide:unit-change",
        {
          unit,
          productId:
            this.root.dataset.productId || ""
        },
        this.root
      );
    }

    /**
     * @param {number} inches
     * @returns {string}
     */
    formatCentimeters(inches) {
      const centimeters = inches * 2.54;

      return Number.isInteger(centimeters)
        ? String(centimeters)
        : centimeters.toFixed(1);
    }

    /**
     * @param {number} inches
     * @returns {string}
     */
    formatInches(inches) {
      return Number.isInteger(inches)
        ? String(inches)
        : inches.toFixed(1);
    }

    destroy() {
      document.removeEventListener(
        "click",
        this.handleDocumentClick
      );

      this.closeButtons.forEach((button) => {
        button.removeEventListener(
          "click",
          this.handleCloseClick
        );
      });

      this.dialog?.removeEventListener(
        "click",
        this.handleDialogClick
      );

      this.dialog?.removeEventListener(
        "close",
        this.handleDialogClose
      );

      this.unitButtons.forEach((button) => {
        button.removeEventListener(
          "click",
          this.handleUnitClick
        );
      });

      if (this.dialog?.open) {
        this.dialog.close();
      }

      delete this.root.dataset.hfInitialized;
    }
  }

  function initializeSizeGuides(
    context = document
  ) {
    if (!window.HFDS) {
      return;
    }

    window.HFDS
      .queryAll(ROOT_SELECTOR, context)
      .forEach((root) => {
        if (!(root instanceof HTMLElement)) {
          return;
        }

        if (root.dataset.hfInitialized === "true") {
          return;
        }

        root.hfSizeGuide =
          new HFSizeGuide(root);
      });
  }

  window.HFDS?.ready(() => {
    initializeSizeGuides(document);
  });

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initializeSizeGuides(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      window.HFDS
        ?.queryAll(ROOT_SELECTOR, event.target)
        .forEach((root) => {
          root.hfSizeGuide?.destroy();
        });
    }
  );

  window.HFDSSizeGuide = Object.freeze({
    version: "1.0.0",
    initialize: initializeSizeGuides
  });
})();